// On-device ingredient recognition: no model, no network, works offline on every platform.
//   - findMentions: which known ingredients a text names, and the amount written in front ("200 g flour").
//   - detectIngredients: a recipe written as instructions only → its ingredient list with amounts.
//   - linkSteps: which of a recipe's ingredients each step uses (the step's ingredient lines).
//   - matchName: one name ("Eggs", "olive oil") → a known ingredient, for AI drafts and assistant proposals.
// Matching uses titles and synonyms (English and German in the catalog), folded, with simple plurals.
// It is deliberately conservative: an unknown word is never guessed. The server AI (ai.ts) covers free text
// this cannot read; the result of either is reviewed by the person in the editor.
import { fold, isLetter } from './text.ts';
import { fromNumber, parseAmount, toNumber, type Amount } from './quantity.ts';
import { SEED_UNITS, type UnitRef } from './units.ts';
import type { Id, IngredientLine, Recipe, Step } from '../types';

export type Matchable = { id: Id; title: string; synonyms: string[] };
export type Matcher = { terms: Map<string, Id>; maxWords: number };
export type Mention = { id: Id; word: number; words: number; amount: Amount | null; unit: UnitRef };

/** Folded words of a text, digits split from letters ("200g" → "200", "g"), fractions kept ("1/2", "1,5", "½"). */
export function words(text: string): string[] {
  const raw = fold(text).match(/[\p{L}\p{M}\p{N}½¼¾⅓⅔⅛]+(?:[.,/][\p{N}]+)*/gu) ?? [];
  return raw.flatMap(token => {
    const parts: string[] = [];
    let cur = '';
    let letter: boolean | null = null;
    for (const ch of token) {
      const l = isLetter(ch);
      if (letter !== null && l !== letter && !/[.,/]/.test(ch)) {
        parts.push(cur);
        cur = '';
      }
      cur += ch;
      if (!/[.,/]/.test(ch)) letter = l;
    }
    if (cur) parts.push(cur);
    return parts;
  });
}

/** Spelling variants of one folded word: English and German plurals both ways. */
function variants(word: string): string[] {
  const out = new Set([word]);
  if (word.length > 3) {
    if (word.endsWith('ies')) out.add(`${word.slice(0, -3)}y`);
    if (word.endsWith('oes') || word.endsWith('ches') || word.endsWith('shes')) out.add(word.slice(0, -2));
    if (word.endsWith('s')) out.add(word.slice(0, -1));
    if (word.endsWith('en') || word.endsWith('n')) out.add(word.replace(/e?n$/, ''));
    if (word.endsWith('e')) out.add(word.slice(0, -1));
  }
  return [...out];
}

const termKey = (ws: string[]) => ws.join(' ');

/** Builds the lookup once per ingredient set. Later items win ties, so pass user items after built-ins. */
export function buildMatcher(items: Matchable[]): Matcher {
  const exact = new Map<string, Id>();
  const loose = new Map<string, Id>();
  let maxWords = 1;
  const add = (phrase: string, id: Id) => {
    const ws = words(phrase);
    if (!ws.length || ws.some(w => /^\d/.test(w))) return;
    maxWords = Math.max(maxWords, ws.length);
    exact.set(termKey(ws), id);
    const last = ws[ws.length - 1];
    for (const v of variants(last)) loose.set(termKey([...ws.slice(0, -1), v]), id);
  };
  for (const it of items) for (const phrase of [it.title, ...it.synonyms]) add(phrase, it.id);
  // A real title or synonym always beats another item's plural variant.
  return { terms: new Map([...loose, ...exact]), maxWords };
}

const UNIT_TERMS: Map<string, UnitRef> = (() => {
  const m = new Map<string, UnitRef>();
  for (const u of SEED_UNITS) for (const term of [u.symbol, u.title, ...u.searchTerms]) if (!m.has(termKey(words(term)))) m.set(termKey(words(term)), { seed: u.key });
  // Common German and short forms the seed list does not carry.
  const extra: [string, UnitRef][] = [['el', { seed: 'metricTablespoon' }], ['essloffel', { seed: 'metricTablespoon' }], ['tl', { seed: 'metricTeaspoon' }], ['teeloffel', { seed: 'metricTeaspoon' }],
    ['gramm', { seed: 'gram' }], ['liter', { seed: 'liter' }], ['tasse', { seed: 'usCup' }], ['tassen', { seed: 'usCup' }]];
  for (const [k, v] of extra) m.set(k, v);
  return m;
})();
const FILLER = new Set(['of', 'the', 'a', 'an', 'von', 'der', 'die', 'das', 'fresh', 'frische', 'frischen', 'frisches', 'ripe']);

function amountAt(ws: string[], i: number): Amount | null {
  if (i < 0) return null;
  // "1 1/2" reads as one amount.
  if (i >= 1 && ws[i].includes('/')) {
    const two = parseAmount(`${ws[i - 1]} ${ws[i]}`);
    if (two) return two;
  }
  return /^[\d½¼¾⅓⅔⅛]/.test(ws[i]) ? parseAmount(ws[i]) : null;
}

/** Known ingredients named in `text`, longest phrase first, each with the amount and unit written just before it. */
export function findMentions(text: string, m: Matcher): Mention[] {
  const ws = words(text);
  const found: Mention[] = [];
  let i = 0;
  while (i < ws.length) {
    let hit: { id: Id; n: number } | null = null;
    for (let n = Math.min(m.maxWords, ws.length - i); n >= 1 && !hit; n--) {
      const ws2 = ws.slice(i, i + n);
      const last = ws2[n - 1];
      for (const v of variants(last)) {
        const id = m.terms.get(termKey([...ws2.slice(0, -1), v]));
        if (id) {
          hit = { id, n };
          break;
        }
      }
    }
    if (!hit) {
      i += 1;
      continue;
    }
    // Look back over fillers for "<amount> <unit>" or "<amount>".
    let j = i - 1;
    while (j >= 0 && FILLER.has(ws[j])) j--;
    let unit: UnitRef = null;
    for (let n = 3; n >= 1 && !unit; n--) {
      if (j - n + 1 < 0) continue;
      const u = UNIT_TERMS.get(termKey(ws.slice(j - n + 1, j + 1)));
      if (u) {
        unit = u;
        j -= n;
      }
    }
    const amount = amountAt(ws, j);
    found.push({ id: hit.id, word: i, words: hit.n, amount, unit: amount ? unit : null });
    i += hit.n;
  }
  return found;
}

/** One name → a known ingredient: the whole name first, then the longest ingredient named inside it. */
export function matchName(name: string, m: Matcher): Id | null {
  const ws = words(name);
  for (const v of variants(ws[ws.length - 1] ?? '')) {
    const id = m.terms.get(termKey([...ws.slice(0, -1), v]));
    if (id) return id;
  }
  const inside = findMentions(name, m).sort((a, b) => b.words - a.words);
  return inside[0]?.id ?? null;
}

const sameUnit = (a: UnitRef, b: UnitRef) => JSON.stringify(a) === JSON.stringify(b);
const stepText = (s: Pick<Step, 'title' | 'explainer'>) => `${s.title}\n${s.explainer}`;

export type DetectedIngredient = { ingredientId: Id; amount: Amount | null; unit: UnitRef; steps: number[] };

/**
 * Instructions only → the ingredients they use. Amounts written in several steps add up when the unit
 * matches ("100 g sugar … the remaining 50 g sugar" → 150 g); otherwise the first stated amount counts.
 */
export function detectIngredients(steps: Pick<Step, 'title' | 'explainer'>[], m: Matcher): DetectedIngredient[] {
  const out = new Map<Id, DetectedIngredient>();
  steps.forEach((s, index) => {
    for (const hit of findMentions(stepText(s), m)) {
      const d = out.get(hit.id) ?? { ingredientId: hit.id, amount: null, unit: null, steps: [] };
      if (!d.steps.includes(index)) d.steps.push(index);
      if (hit.amount) {
        if (!d.amount) Object.assign(d, { amount: hit.amount, unit: hit.unit });
        else if (sameUnit(d.unit, hit.unit)) d.amount = fromNumber(toNumber(d.amount) + toNumber(hit.amount));
      }
      out.set(hit.id, d);
    }
  });
  return [...out.values()];
}

/** Adds detected ingredients a recipe does not list yet (amount 1 without a unit when none was written). */
export function withDetectedIngredients(recipe: Recipe, m: Matcher): { recipe: Recipe; added: Id[] } {
  const listed = new Set(recipe.ingredients.map(l => l.ingredientId));
  const added = detectIngredients(recipe.steps, m).filter(d => !listed.has(d.ingredientId));
  const lines: IngredientLine[] = added.map(d => ({ ingredientId: d.ingredientId, amount: d.amount ?? { value: 1, places: 0 }, unit: d.amount ? d.unit : null }));
  return { recipe: { ...recipe, ingredients: [...recipe.ingredients, ...lines] }, added: added.map(d => d.ingredientId) };
}

/**
 * Links each step to the recipe ingredients it adds. Only the recipe's own ingredients are matched, so
 * "flour" means this recipe's flour. A step that states an amount gets that amount; the first step that
 * names an ingredient without an amount gets what is left of the recipe's amount; later bare mentions
 * ("fold in the flour") are not linked again once everything was used. Links to ingredients the text never
 * names (set by hand) are kept.
 */
export function linkSteps(recipe: Recipe, items: Record<Id, Matchable>): Recipe {
  const own = recipe.ingredients.map(l => items[l.ingredientId]).filter((x): x is Matchable => !!x);
  const m = buildMatcher(own);
  const mentions = recipe.steps.map(s => findMentions(stepText(s), m));
  const named = new Set(mentions.flat().map(x => x.id));
  const left = new Map(recipe.ingredients.map(l => [l.ingredientId, { amount: toNumber(l.amount), unit: l.unit }]));
  // Stated amounts first, so "what is left" is known before bare mentions take it.
  for (const hits of mentions) for (const h of hits) {
    const rest = left.get(h.id);
    if (h.amount && rest && sameUnit(rest.unit, h.unit)) rest.amount = Math.max(0, rest.amount - toNumber(h.amount));
  }
  const steps = recipe.steps.map((s, i) => {
    const lines: IngredientLine[] = [];
    for (const h of mentions[i]) {
      if (lines.some(l => l.ingredientId === h.id)) continue;
      const line = recipe.ingredients.find(l => l.ingredientId === h.id)!;
      if (h.amount) {
        lines.push({ ingredientId: h.id, amount: h.amount, unit: h.unit ?? line.unit });
        continue;
      }
      const rest = left.get(h.id)!;
      if (rest.amount <= 0) continue;
      lines.push({ ingredientId: h.id, amount: fromNumber(rest.amount), unit: line.unit });
      rest.amount = 0;
    }
    const manual = s.ingredients.filter(l => !named.has(l.ingredientId) && !lines.some(x => x.ingredientId === l.ingredientId));
    return { ...s, ingredients: [...lines, ...manual] };
  });
  return { ...recipe, steps };
}

/** A unit written as text ("g", "tbsp", "cups", "EL") → a seed unit, or null. */
export function unitFromText(text: string | null): UnitRef {
  if (!text) return null;
  return UNIT_TERMS.get(termKey(words(text))) ?? null;
}
